import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Navigation from "@/components/Navigation";
import ChineseZodiacCalculator from "@/components/ChineseZodiacCalculator";
import WesternZodiacCalculator from "@/components/WesternZodiacCalculator";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Sparkles, Heart, BookOpen, Star } from "lucide-react";
import { createOrganizationSchema } from "@/utils/schemaHelpers";

const Home = () => {
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Dstino - Horóscopo Diario, Semanal y Mensual Gratis",
    "url": window.location.origin,
    "description": "Horóscopo diario, semanal y mensual gratis para los 12 signos del zodiaco: Aries, Tauro, Géminis, Cáncer, Leo, Virgo, Libra, Escorpio, Sagitario, Capricornio, Acuario y Piscis. Predicciones de amor, trabajo, salud. Test de compatibilidad zodiacal, tarot gratis y horóscopo chino.",
    "inLanguage": "es-ES",
    "keywords": "horóscopo, horóscopo diario, horóscopo semanal, horóscopo mensual, signos del zodiaco, Aries, Tauro, Géminis, Cáncer, Leo, Virgo, Libra, Escorpio, Sagitario, Capricornio, Acuario, Piscis, compatibilidad zodiacal, tarot gratis, horóscopo chino",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${window.location.origin}/horoscopo?sign={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Signos del Zodiaco",
    "description": "Los 12 signos del zodiaco con horóscopo diario, semanal y mensual",
    "itemListElement": zodiacSigns.map((sign, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "WebPage",
        "@id": `${window.location.origin}${sign.path}`,
        "name": `Horóscopo ${sign.name}`,
        "description": `Horóscopo diario, semanal y mensual para ${sign.name} (${sign.dates})`,
        "url": `${window.location.origin}${sign.path}`
      }
    }))
  };

  const organizationSchema = createOrganizationSchema();

  return (
    <>
      <SchemaMarkup schema={websiteSchema} />
      <SchemaMarkup schema={organizationSchema} />
      <SchemaMarkup schema={itemListSchema} />
      
      <div className="min-h-screen">
        <Navigation />
        
        {/* Hero Section */}
        <main>
          <section className="pt-24 pb-16 md:pt-32 md:pb-20 px-4 star-pattern">
            <div className="container mx-auto text-center max-w-5xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-glow leading-[1.15]">
                Horóscopo Diario Gratis
              </h1>
              <p className="text-base md:text-lg mb-3 text-foreground/90 max-w-xl mx-auto leading-relaxed">
                Consulta tu horóscopo diario, semanal y mensual gratis para todos los signos del zodiaco.
              </p>
              <p className="text-sm md:text-base mb-8 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Predicciones de amor, trabajo, salud y dinero. Tarot gratis y test de compatibilidad zodiacal.
              </p>
              <div className="flex gap-3 justify-center flex-wrap mt-8">
                <WesternZodiacCalculator />
                <ChineseZodiacCalculator />
                <Link to="/tarot">
                  <Button size="lg" variant="outline" className="text-base md:text-lg border-primary/50 hover:bg-primary/10">
                    Consultar Tarot
                  </Button>
                </Link>
              </div>
            </div>
          </section>

        {/* Services Section */}
        <section className="py-16 md:py-20 px-4 bg-background/50">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-3 text-primary">
                Guías que te pueden ayudar
              </h2>
              <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto">
                Explora nuestras herramientas astrológicas para descubrir tu destino
              </p>
            </div>
          
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <Link to="/horoscopo">
                <Card className="p-5 md:p-6 hover:card-glow transition-all duration-300 cursor-pointer gradient-celestial h-full">
                  <Star className="w-10 h-10 md:w-12 md:h-12 text-primary mb-3" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Horóscopo Diario</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Predicciones personalizadas para cada signo zodiacal. Actualizado diariamente.
                  </p>
                </Card>
              </Link>

              <Link to="/horoscopo">
                <Card className="p-5 md:p-6 hover:card-glow transition-all duration-300 cursor-pointer gradient-celestial h-full">
                  <Sparkles className="w-10 h-10 md:w-12 md:h-12 text-secondary mb-3" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Horóscopo Semanal</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Planifica tu semana con las predicciones astrales más precisas.
                  </p>
                </Card>
              </Link>

              <Link to="/compatibilidad">
                <Card className="p-5 md:p-6 hover:card-glow transition-all duration-300 cursor-pointer gradient-celestial h-full">
                  <Heart className="w-10 h-10 md:w-12 md:h-12 text-accent mb-3" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Compatibilidad</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Descubre tu compatibilidad en amor, amistad y trabajo con otros signos.
                  </p>
                </Card>
              </Link>

              <Link to="/tarot">
                <Card className="p-5 md:p-6 hover:card-glow transition-all duration-300 cursor-pointer gradient-celestial h-full">
                  <BookOpen className="w-10 h-10 md:w-12 md:h-12 text-primary mb-3" />
                  <h3 className="text-xl md:text-2xl font-bold mb-2">Tarot Gratis</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Tirada de 3 cartas para descubrir el amor y tu destino.
                  </p>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* Zodiac Signs Quick Access */}
        <section className="py-16 md:py-20 px-4 gradient-mystical">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Los 12 Signos del Zodiaco
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Consulta tu horóscopo personalizado según tu signo zodiacal. Predicciones diarias, semanales y mensuales para descubrir qué te deparan los astros.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {zodiacSigns.map((sign) => (
              <Link key={sign.name} to={sign.path}>
                <Card className="p-3 md:p-4 text-center hover:card-glow transition-all duration-300 cursor-pointer gradient-celestial">
                  <div className="text-3xl md:text-4xl mb-1.5" aria-label={`Signo ${sign.name}`}>{sign.emoji}</div>
                  <h3 className="font-bold text-sm md:text-base">Horóscopo {sign.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">{sign.dates}</p>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

        {/* Chinese Horoscope Section */}
        <section className="py-16 md:py-20 px-4 bg-card/30">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Horóscopo Chino 2025
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Descubre las predicciones del horóscopo chino para tu signo animal: Rata, Buey, Tigre, Conejo, Dragón, Serpiente, Caballo, Cabra, Mono, Gallo, Perro y Cerdo según tu año de nacimiento.
            </p>
            <Link to="/horoscopo-chino">
              <Button size="lg" className="text-base md:text-lg">
                Ver Horóscopo Chino Completo
              </Button>
            </Link>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-16 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <article className="space-y-10">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">¿Qué es el Horóscopo?</h2>
                <div className="w-16 h-1 bg-primary mx-auto"></div>
              </div>

              <div className="space-y-8">
                <div className="bg-card/40 rounded-lg p-6 md:p-8 border border-border/40">
                  <p className="text-sm md:text-base text-foreground/90 leading-relaxed text-justify">
                    El horóscopo es una predicción astrológica basada en la posición de los astros y planetas en el momento de tu nacimiento. Los 12 signos del zodiaco (Aries, Tauro, Géminis, Cáncer, Leo, Virgo, Libra, Escorpio, Sagitario, Capricornio, Acuario y Piscis) representan diferentes características de personalidad y tendencias en la vida.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h3 className="text-xl md:text-2xl font-bold text-primary text-center">Horóscopo Diario Gratis</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed text-justify">
                    Nuestro horóscopo diario te ofrece predicciones actualizadas cada día para tu signo zodiacal. Consulta las tendencias en amor, trabajo, salud y dinero. Cada signo recibe predicciones personalizadas basadas en el movimiento de los planetas y las influencias astrales del día.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h3 className="text-xl md:text-2xl font-bold text-primary text-center">Compatibilidad Zodiacal</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed text-justify">
                    Descubre tu compatibilidad amorosa con otros signos del zodiaco. El test de compatibilidad analiza la afinidad entre signos según sus elementos (fuego, tierra, aire y agua) y características astrológicas. Conoce qué signos son más compatibles contigo en amor, amistad y trabajo.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <h3 className="text-xl md:text-2xl font-bold text-primary text-center">Tarot y Cartas</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed text-justify">
                    Además del horóscopo, ofrecemos lecturas de tarot gratis. El tarot complementa las predicciones astrológicas y te ayuda a tomar decisiones importantes en tu vida. Consulta el tarot del amor, el tarot del trabajo y el tarot predictivo para conocer tu destino.
                  </p>
                </div>
              </div>
            </article>
          </div>
        </section>
        </main>

        {/* Footer */}
        <footer className="pt-24 pb-12 px-4 border-t border-border">
        <div className="container mx-auto text-center relative">
          {/* Floating Arrow Indicator */}
          <div className="flex flex-col items-center gap-2 animate-fade-in mb-12">
            <p className="text-xs md:text-sm text-muted-foreground/80 font-medium px-3 py-1.5 bg-card/60 backdrop-blur-sm rounded-full border border-border/50 shadow-sm">
              Puedes seguirnos en Spotify
            </p>
            <div className="animate-bounce">
              <svg 
                className="w-5 h-5 text-primary/50" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
          
          <a 
            href="https://open.spotify.com/show/7makyw1CJREHr2CwcTMeKc?si=WfsNGM95Qae9yGq_WPgKEQ" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mb-6 hover:text-primary transition-colors hover:scale-110 transform duration-300"
          >
            <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </a>
          <p className="text-muted-foreground">&copy; dkensgroup 2025</p>
        </div>
      </footer>
      </div>
    </>
  );
};

const zodiacSigns = [
  { name: "Aries", emoji: "♈", dates: "Mar 21 - Abr 19", path: "/horoscopo-aries" },
  { name: "Tauro", emoji: "♉", dates: "Abr 20 - May 20", path: "/horoscopo-tauro" },
  { name: "Géminis", emoji: "♊", dates: "May 21 - Jun 20", path: "/horoscopo-geminis" },
  { name: "Cáncer", emoji: "♋", dates: "Jun 21 - Jul 22", path: "/horoscopo-cancer" },
  { name: "Leo", emoji: "♌", dates: "Jul 23 - Ago 22", path: "/horoscopo-leo" },
  { name: "Virgo", emoji: "♍", dates: "Ago 23 - Sep 22", path: "/horoscopo-virgo" },
  { name: "Libra", emoji: "♎", dates: "Sep 23 - Oct 22", path: "/horoscopo-libra" },
  { name: "Escorpio", emoji: "♏", dates: "Oct 23 - Nov 21", path: "/horoscopo-escorpio" },
  { name: "Sagitario", emoji: "♐", dates: "Nov 22 - Dic 21", path: "/horoscopo-sagitario" },
  { name: "Capricornio", emoji: "♑", dates: "Dic 22 - Ene 19", path: "/horoscopo-capricornio" },
  { name: "Acuario", emoji: "♒", dates: "Ene 20 - Feb 18", path: "/horoscopo-acuario" },
  { name: "Piscis", emoji: "♓", dates: "Feb 19 - Mar 20", path: "/horoscopo-piscis" },
];

export default Home;
